import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import {
  MessageCircle, Mail, Phone, Bell, Send, CheckCircle, Eye, MousePointer,
  XCircle, Tag, Ban, CreditCard, Wallet, StickyNote, FileText, Webhook,
  ClipboardList, MessageSquare, Hash,
} from 'lucide-react';

const actionIcons = {
  whatsapp: MessageCircle,
  email: Mail,
  sms: Phone,
  push: Bell,
  notification: Bell,
  add_tag: Tag,
  remove_tag: Tag,
  cancel_order: Ban,
  add_store_credit: CreditCard,
  remove_store_credit: Wallet,
  update_customer_tag: Hash,
  update_order_note: StickyNote,
  http_webhook: Webhook,
  create_task: ClipboardList,
  send_slack: MessageSquare,
  send_discord: MessageSquare,
  default: MessageCircle,
};

const actionLabels = {
  whatsapp: 'Send WhatsApp',
  email: 'Send Email',
  sms: 'Send SMS',
  push: 'Send Push Notification',
  notification: 'Push Notification',
  add_tag: 'Add Tag',
  remove_tag: 'Remove Tag',
  cancel_order: 'Cancel Order',
  add_store_credit: 'Add Store Credit',
  remove_store_credit: 'Deduct Store Credit',
  update_customer_tag: 'Customer Tag',
  update_order_note: 'Order Note',
  http_webhook: 'HTTP Webhook',
  create_task: 'Create Task',
  send_slack: 'Send to Slack',
  send_discord: 'Send to Discord',
};

// Outcome-branch handles per action type. The first outcome ("on_send") is the
// immediate / default path and is also used for backward compatibility with
// legacy edges that have no sourceHandle.
const ACTION_OUTCOMES = {
  whatsapp: [
    { id: 'on_send', label: 'On Send', color: '#94a3b8' },
    { id: 'on_delivery', label: 'On Delivery', color: '#3b82f6' },
    { id: 'on_read', label: 'On Read', color: '#a855f7' },
    { id: 'on_click', label: 'On Click', color: '#06b6d4' },
    { id: 'on_failure', label: 'On Failure', color: '#ef4444' },
  ],
  email: [
    { id: 'on_send', label: 'On Send', color: '#94a3b8' },
    { id: 'on_delivery', label: 'On Delivery', color: '#3b82f6' },
    { id: 'on_open', label: 'On Open', color: '#a855f7' },
    { id: 'on_click', label: 'On Click', color: '#06b6d4' },
    { id: 'on_unsubscribe', label: 'On Unsubscribe', color: '#f59e0b' },
    { id: 'on_bounce', label: 'On Bounce', color: '#ef4444' },
  ],
  sms: [
    { id: 'on_send', label: 'On Send', color: '#94a3b8' },
    { id: 'on_delivery', label: 'On Delivery', color: '#3b82f6' },
    { id: 'on_failure', label: 'On Failure', color: '#ef4444' },
  ],
  // Web Push has only deliver/click/fail observable from the SW.
  push: [
    { id: 'on_send', label: 'On Send', color: '#94a3b8' },
    { id: 'on_click', label: 'On Click', color: '#06b6d4' },
    { id: 'on_failure', label: 'On Failure', color: '#ef4444' },
  ],
  // HTTP webhook: success vs failure branches
  http_webhook: [
    { id: 'on_send', label: 'On Success', color: '#22c55e' },
    { id: 'on_failure', label: 'On Failure', color: '#ef4444' },
  ],
  // Single-outcome action nodes (default bottom handle)
  add_tag: [{ id: 'on_send', label: '', color: '#94a3b8' }],
  remove_tag: [{ id: 'on_send', label: '', color: '#94a3b8' }],
  cancel_order: [{ id: 'on_send', label: '', color: '#94a3b8' }],
  add_store_credit: [{ id: 'on_send', label: '', color: '#94a3b8' }],
  remove_store_credit: [{ id: 'on_send', label: '', color: '#94a3b8' }],
  update_customer_tag: [{ id: 'on_send', label: '', color: '#94a3b8' }],
  update_order_note: [{ id: 'on_send', label: '', color: '#94a3b8' }],
  create_task: [{ id: 'on_send', label: '', color: '#94a3b8' }],
  send_slack: [{ id: 'on_send', label: '', color: '#94a3b8' }],
  send_discord: [{ id: 'on_send', label: '', color: '#94a3b8' }],
};

function ActionNodeComponent({ data, selected }) {
  const actionType = data.action_type || 'whatsapp';
  const Icon = actionIcons[actionType] || actionIcons.default;
  const label = actionLabels[actionType] || data.label || 'Action';
  const templateName = data.template_name || 'No template selected';
  const showAnalytics = data.showAnalytics;
  const analytics = data.analytics;

  const outcomes = ACTION_OUTCOMES[actionType] || ACTION_OUTCOMES.whatsapp;
  const isMultiOutcome = outcomes.length > 1;

  // Subtitle based on action type
  let subtitle = null;
  if (actionType === 'whatsapp' || actionType === 'email') {
    subtitle = templateName;
  } else if (actionType === 'add_tag' || actionType === 'remove_tag' || actionType === 'update_customer_tag') {
    subtitle = data.tag_value ? `"${data.tag_value}"` : 'No tag set';
  } else if (actionType === 'cancel_order') {
    subtitle = `${data.sync_to_shopify ? 'Local + Shopify' : 'Local only'} · ${data.restock ? 'restock' : 'no restock'}`;
  } else if (actionType === 'add_store_credit' || actionType === 'remove_store_credit') {
    subtitle = data.credit_amount ? `${data.credit_amount}${data.credit_expires_in_days ? ` · ${data.credit_expires_in_days}d` : ''}` : 'No amount set';
  } else if (actionType === 'update_order_note') {
    subtitle = data.note_text ? data.note_text.slice(0, 30) + (data.note_text.length > 30 ? '…' : '') : 'No note set';
  } else if (actionType === 'http_webhook') {
    subtitle = data.webhook_url ? (data.webhook_method || 'POST') + ' ' + data.webhook_url.slice(0, 28) + '…' : 'No URL set';
  } else if (actionType === 'create_task') {
    subtitle = data.task_title ? data.task_title.slice(0, 30) : 'No title';
  } else if (actionType === 'send_slack' || actionType === 'send_discord') {
    subtitle = data.webhook_url ? 'Webhook configured' : 'No webhook URL';
  }

  return (
    <div
      className={`
        relative px-4 py-3 rounded-xl border-2 min-w-[220px]
        bg-gradient-to-br from-amber-500/20 to-amber-600/10
        ${selected ? 'border-wt-gold shadow-lg shadow-amber-500/30' : 'border-amber-500/50'}
        transition-all duration-200
      `}
    >
      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !bg-wt-gold !border-2 !border-amber-600"
      />

      {/* Node Header */}
      <div className="flex items-center gap-2 mb-1">
        <div className="p-1.5 rounded-lg bg-amber-500/30">
          <Icon className="h-4 w-4 text-wt-gold" />
        </div>
        <span className="text-xs font-medium text-wt-gold uppercase tracking-wide">
          Action
        </span>
      </div>

      {/* Node Label */}
      <div className="text-sm font-semibold text-white mb-1">
        {label}
      </div>

      {/* Subtitle */}
      {subtitle && (
        <div className="text-xs text-wt-text-muted truncate max-w-[200px]">
          {subtitle}
        </div>
      )}

      {/* Inline Analytics Stats (whatsapp/email) */}
      {showAnalytics && (actionType === 'whatsapp' || actionType === 'email') && (
        <div className="mt-2 pt-2 border-t border-amber-500/30">
          <div className="grid grid-cols-5 gap-1 text-center">
            <div className="flex flex-col items-center">
              <Send className="h-3 w-3 text-blue-400 mb-0.5" />
              <span className="text-xs font-bold text-white">{analytics?.sent || 0}</span>
              <span className="text-[10px] text-wt-text-muted">Sent</span>
            </div>
            <div className="flex flex-col items-center">
              <CheckCircle className="h-3 w-3 text-green-400 mb-0.5" />
              <span className="text-xs font-bold text-white">{analytics?.delivered || 0}</span>
              <span className="text-[10px] text-wt-text-muted">Dlvd</span>
            </div>
            <div className="flex flex-col items-center">
              <Eye className="h-3 w-3 text-purple-400 mb-0.5" />
              <span className="text-xs font-bold text-white">{analytics?.read || 0}</span>
              <span className="text-[10px] text-wt-text-muted">Read</span>
            </div>
            <div className="flex flex-col items-center">
              <MousePointer className="h-3 w-3 text-cyan-400 mb-0.5" />
              <span className="text-xs font-bold text-white">{analytics?.clicked || 0}</span>
              <span className="text-[10px] text-wt-text-muted">Click</span>
            </div>
            <div className="flex flex-col items-center">
              <XCircle className={`h-3 w-3 mb-0.5 ${(analytics?.failed || 0) > 0 ? 'text-red-400' : 'text-wt-text-muted'}`} />
              <span className={`text-xs font-bold ${(analytics?.failed || 0) > 0 ? 'text-red-400' : 'text-white'}`}>{analytics?.failed || 0}</span>
              <span className="text-[10px] text-wt-text-muted">Failed</span>
            </div>
          </div>
        </div>
      )}

      {/* Output Handles */}
      {!isMultiOutcome && (
        <Handle
          type="source"
          position={Position.Bottom}
          id="on_send"
          className="!w-3 !h-3 !bg-wt-gold !border-2 !border-amber-600"
        />
      )}
      {isMultiOutcome && (
        <div className="mt-3 pt-3 border-t border-amber-500/30">
          {outcomes.map((oc) => (
            <div
              key={oc.id}
              className="relative flex items-center justify-end gap-2 py-1 pr-4 text-[11px] font-medium"
            >
              <span style={{ color: oc.color }}>{oc.label}</span>
              {/* Handle is anchored inside this row so it stays glued to the
                  card's right edge regardless of header/content height. */}
              <Handle
                type="source"
                position={Position.Right}
                id={oc.id}
                className="!border-2"
                style={{
                  width: 10,
                  height: 10,
                  right: -5,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: oc.color,
                  borderColor: oc.color,
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export const ActionNode = memo(ActionNodeComponent);
